<template>
    <div class="company-management-wrapper">
        <!-- Breadcrumb -->
        <div class="breadcrumb">
            <router-link to="/">Dashboard</router-link>
            <span class="crumb-sep">/</span>
            <span class="crumb-current">Companies</span>
        </div>

        <div class="management-header">
            <div>
                <h1 class="page-title">Company Management</h1>
                <p class="page-subtitle">Manage your active client companies and their details.</p>
            </div>
            <div class="header-actions">
                <button class="btn-add-company btn-secondary" @click="showPaymentLinkPanel = true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                    Generate Payment Link
                </button>
                <button class="btn-add-company" @click="showModal = true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Company
                </button>
            </div>
        </div>

        <!-- Table Card -->
        <div class="table-glass-card">
            <table class="premium-table">
                <thead>
                    <tr>
                        <th>Code</th>
                        <th>Company Name</th>
                        <th>Primary Contact</th>
                        <th>Contact Email</th>
                        <th>Status</th>
                        <th>Subscription</th>
                        <th class="text-right">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="company in companies" :key="company.id" class="table-row-hover">
                        <td>
                            <div class="code-cell">
                                <span class="text-id">#{{ company.code }}</span>
                                <span v-if="company.is_super_admin" class="super-admin-tag">★ Super Admin</span>
                            </div>
                        </td>
                        <td>
                            <router-link :to="'/companies/' + company.id" class="company-name-link">
                                {{ company.name }}
                            </router-link>
                        </td>
                        <td class="cell-primary">{{ company.contact_person_name }}</td>
                        <td class="cell-secondary">{{ company.contact_person_email }}</td>
                        <td>
                            <span class="status-badge" :class="company.is_active ? 'active' : 'inactive'">
                                <i class="status-dot"></i>
                                {{ company.is_active ? 'Active' : 'Inactive' }}
                            </span>
                        </td>
                        <td>
                            <button class="subscription-btn" :class="planStatusClass(company)" @click="openSubscription(company)">
                                {{ company.plan ? company.plan.name : 'No plan assigned' }}
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            </button>
                            <div v-if="company.plan" class="subscription-dates">
                                {{ formatDate(company.plan_assigned_date) }} → {{ formatDate(company.plan_expiry_date) }}
                            </div>
                        </td>
                        <td>
                            <div class="row-actions">
                                <button class="action-btn" @click="toggleStatus(company)"
                                    :title="company.is_active ? 'Deactivate' : 'Activate'"
                                    :class="{ danger: company.is_active }">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke-width="2">
                                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                                        <line x1="12" y1="2" x2="12" y2="12" />
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="companies.length === 0">
                        <td colspan="7" class="empty-state">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                                <path d="M3 21h18" />
                                <path d="M3 7v1a3 3 0 0 0 6 0V7m6 0v1a3 3 0 0 0 6 0V7m-6 0h6M9 7h6M9 7V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4" />
                            </svg>
                            <p>No companies registered yet.</p>
                            <button @click="showModal = true" class="empty-cta">Add your first company</button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <AppPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="fetchCompanies" />
        </div>

        <SubscriptionPanel
            :show="showSubscriptionPanel"
            :company="selectedCompanyForPlan"
            @close="showSubscriptionPanel = false"
            @updated="fetchCompanies(currentPage)"
        />

        <PaymentLinkPanel
            :show="showPaymentLinkPanel"
            @close="showPaymentLinkPanel = false"
        />

        <!-- Add Company Modal (Premium) -->
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
            <div class="modal-content">
                <!-- Header -->
                <div class="modal-header">
                    <div class="modal-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M3 21h18" />
                            <path d="M3 7v1a3 3 0 0 0 6 0V7m6 0v1a3 3 0 0 0 6 0V7m-6 0h6M9 7h6M9 7V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4" />
                        </svg>
                    </div>
                    <div>
                        <h2>Add New Company</h2>
                        <p>Register a new client company on the platform</p>
                    </div>
                </div>

                <div class="modal-body">
                    <form @submit.prevent="addCompany" class="modal-form">
                        <div class="form-group">
                            <label class="form-label">Company Name</label>
                            <input type="text" v-model="newCompany.name" required placeholder="Enter company name"
                                class="form-input-premium">
                        </div>

                        <div class="form-group">
                            <label class="form-label">Primary Contact Person</label>
                            <input type="text" v-model="newCompany.person" required
                                placeholder="Full name of contact person" class="form-input-premium">
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Email Address</label>
                                <input type="email" v-model="newCompany.email" required placeholder="email@company.com"
                                    class="form-input-premium">
                            </div>
                            <div class="form-group">
                                <label class="form-label">Mobile Number</label>
                                <input type="tel" v-model="newCompany.mobile" required placeholder="10-digit number"
                                    maxlength="10" @input="newCompany.mobile = newCompany.mobile.replace(/[^0-9]/g, '')"
                                    class="form-input-premium">
                            </div>
                        </div>

                        <div v-if="validationError" class="validation-error">
                            {{ validationError }}
                        </div>

                        <div class="modal-actions">
                            <button type="button" @click="showModal = false" class="btn-cancel">
                                Cancel
                            </button>
                            <button type="submit" :disabled="loading" class="btn-submit">
                                <span v-if="loading" class="spinner-small"></span>
                                {{ loading ? 'REGISTERING...' : 'REGISTER COMPANY' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Notification Toast -->
        <div v-if="successMessage" class="toast-notification">
            <div class="toast-body">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{{ successMessage }}</span>
            </div>
        </div>

        <!-- Error Toast -->
        <div v-if="errorMessage" class="toast-notification">
            <div class="toast-body error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{{ errorMessage }}</span>
                <button class="toast-close-btn" @click="errorMessage = ''">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import AppPagination from './AppPagination.vue';
import SubscriptionPanel from './SubscriptionPanel.vue';
import PaymentLinkPanel from './PaymentLinkPanel.vue';

export default {
    name: 'CompanyManagement',
    components: { AppPagination, SubscriptionPanel, PaymentLinkPanel },
    data() {
        return {
            companies: [],
            showModal: false,
            loading: false,
            validationError: '',
            successMessage: '',
            errorMessage: '',
            newCompany: { name: '', email: '', person: '', mobile: '' },
            currentPage: 1,
            totalPages: 1,
            showSubscriptionPanel: false,
            selectedCompanyForPlan: null,
            showPaymentLinkPanel: false
        }
    },
    async mounted() {
        await this.fetchCompanies();
    },
    methods: {
        showError(message) {
            this.errorMessage = message;
            setTimeout(() => { this.errorMessage = ''; }, 5000);
        },
        openSubscription(company) {
            this.selectedCompanyForPlan = company;
            this.showSubscriptionPanel = true;
        },
        formatDate(dateStr) {
            if (!dateStr) return '—';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        },
        planStatusClass(company) {
            const s = company.plan_status;
            if (s === 'ACTIVE') return 'active';
            if (s === 'EXPIRING_SOON') return 'expiring';
            if (s === 'EXPIRED') return 'expired';
            return 'none';
        },
        async fetchCompanies(page = 1) {
            try {
                this.currentPage = page;
                const response = await axios.get(`${this.$API_URL}/super_admin/companies?page=${page}&page_size=10`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                this.companies = response.data.items || [];
                this.totalPages = response.data.total_pages || 1;
            } catch (error) { console.error(error); }
        },
        async addCompany() {
            if (this.loading) return;
            this.validationError = '';

            // Validation
            if (!this.newCompany.name.trim()) { this.validationError = "Company name is required"; return; }
            if (!this.newCompany.person.trim()) { this.validationError = "Contact person is required"; return; }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.newCompany.email)) { this.validationError = "Invalid email format"; return; }
            if (this.newCompany.mobile.length !== 10) { this.validationError = "Mobile number must be 10 digits"; return; }

            this.loading = true;
            try {
                // Correct Endpoint for admin to register company
                await axios.post(`${this.$API_URL}/company/register`, {
                    name: this.newCompany.name,
                    contact_person_name: this.newCompany.person,
                    contact_person_email: this.newCompany.email,
                    contact_person_mobile: this.newCompany.mobile
                }, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });

                this.successMessage = "Company registered successfully!";
                this.showModal = false;
                this.newCompany = { name: '', email: '', person: '', mobile: '' };
                await this.fetchCompanies();

                setTimeout(() => { this.successMessage = ''; }, 3000);
            } catch (error) {
                this.validationError = error.response?.data?.detail || 'Registration failed. Check if email/name exists.';
            } finally { this.loading = false; }
        },
        async toggleStatus(company) {
            if (this.loading) return;
            const action = company.is_active ? 'deactivate' : 'activate';
            if (!confirm(`Are you sure you want to ${action} ${company.name}?`)) return;

            this.loading = true;
            try {
                await axios.patch(`${this.$API_URL}/super_admin/company/${company.id}/status?is_active=${!company.is_active}`, {}, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` }
                });
                await this.fetchCompanies();
                this.successMessage = `Company ${company.is_active ? 'deactivated' : 'activated'} successfully`;
                setTimeout(() => { this.successMessage = ''; }, 3000);
            } catch (error) {
                this.showError(error.response?.data?.message || error.response?.data?.detail || 'Failed to update status');
            } finally { this.loading = false; }
        },
    }
}
</script>

<style scoped>
.company-management-wrapper {
    font-family: var(--font-sans);
}

/* Breadcrumb */
.breadcrumb {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-sm);
}

.breadcrumb a {
    color: var(--text-secondary);
    text-decoration: none;
    font-weight: 500;
}

.breadcrumb a:hover { color: #0071C5; }

.crumb-sep { color: #cbd5e1; }

.crumb-current { color: #0071C5; font-weight: 700; }

/* Header */
.management-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
}

.page-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.page-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.header-actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.btn-add-company {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: #fff;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: var(--fs-btn);
    font-family: var(--font-sans);
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.35);
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-add-company:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.45);
}

.btn-add-company.btn-secondary {
    background: white;
    color: #0071C5;
    border: 1.5px solid #0071C5;
    box-shadow: none;
}

.btn-add-company.btn-secondary:hover {
    background: rgba(0, 113, 197, 0.06);
    box-shadow: none;
}

/* Table card */
.table-glass-card {
    background: white;
    border-radius: 20px;
    border: 1px solid #eef2f7;
    overflow: hidden;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
}

.premium-table {
    width: 100%;
    border-collapse: collapse;
}

.premium-table thead {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
}

.premium-table th {
    padding: 1.1rem 1.5rem;
    text-align: left;
    color: var(--text-secondary);
    font-size: var(--fs-th);
    font-weight: var(--fw-th);
    text-transform: uppercase;
    letter-spacing: var(--tracking-th);
}

.premium-table th.text-right { text-align: right; }

.premium-table td {
    padding: 1.1rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
}

.table-row-hover { transition: background 0.2s; }
.table-row-hover:hover { background: #f8fafc; }

.code-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.text-id {
    font-family: var(--font-mono);
    font-size: 0.8rem;
    font-weight: 600;
    background: #f1f5f9;
    color: #64748b;
    padding: 4px 8px;
    border-radius: 6px;
    width: fit-content;
}

.super-admin-tag {
    font-size: 0.65rem;
    font-weight: 800;
    color: #04471c;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding-left: 4px;
}

.company-name-link {
    color: #0071C5;
    text-decoration: none;
    font-weight: 700;
    font-size: var(--fs-row-title);
    display: block;
}

.company-name-link:hover { text-decoration: underline; }

.cell-primary {
    color: var(--text-primary);
    font-size: var(--fs-body);
    font-weight: 500;
}

.cell-secondary {
    color: var(--text-secondary);
    font-size: var(--fs-body);
}

.status-badge {
    padding: 4px 10px;
    border-radius: 20px;
    font-size: var(--fs-caption);
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.status-badge.active { background: #ecfdf5; color: #10b981; }
.status-badge.inactive { background: #fef2f2; color: #ef4444; }

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.subscription-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: var(--text-secondary);
    font-size: 0.8rem;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.subscription-btn:hover {
    border-color: #0071C5;
    color: #0071C5;
    background: white;
}

.subscription-btn.active { border-color: rgba(16, 185, 129, 0.3); color: #10b981; background: rgba(16, 185, 129, 0.06); }
.subscription-btn.expiring { border-color: rgba(245, 158, 11, 0.3); color: #d97706; background: rgba(245, 158, 11, 0.06); }
.subscription-btn.expired { border-color: rgba(239, 68, 68, 0.3); color: #ef4444; background: rgba(239, 68, 68, 0.06); }

.subscription-dates {
    margin-top: 6px;
    font-size: 0.72rem;
    color: var(--text-secondary);
    font-weight: 500;
    white-space: nowrap;
}

.row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}

.action-btn {
    padding: 8px;
    border-radius: 8px;
    background: #f8fafc;
    color: #10b981;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn svg { stroke: currentColor; }
.action-btn.danger { color: #ef4444; }

.action-btn:hover {
    border-color: #0071C5;
    color: #0071C5;
    background: white;
}

.empty-state {
    padding: 4rem;
    text-align: center;
    color: #94a3b8;
}

.empty-state svg { opacity: 0.3; margin-bottom: 1rem; }

.empty-state p {
    font-size: var(--fs-body-lg);
    font-weight: 500;
}

.empty-cta {
    color: #0071C5;
    background: none;
    border: none;
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
    font-size: var(--fs-body);
}

/* Modal */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.modal-content {
    background: white;
    width: 100%;
    max-width: 500px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: modalSlideUp 0.3s ease-out;
}

.modal-header {
    background: #f8fafc;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.modal-header h2 {
    margin: 0;
    font-size: var(--fs-h3);
    font-weight: 800;
    color: var(--text-primary);
}

.modal-header p {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-weight: 500;
}

.modal-icon {
    background: #0071C5;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.modal-body { padding: 2rem; }

.modal-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: var(--fw-label);
    letter-spacing: var(--tracking-label);
    text-transform: uppercase;
    color: var(--text-label);
    margin-bottom: 0.4rem;
    display: block;
}

.form-input-premium {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.95rem;
    font-family: var(--font-sans);
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
}

.form-input-premium:focus {
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
    background: white;
}

.validation-error {
    background: #fef2f2;
    color: #ef4444;
    padding: 0.75rem;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: center;
    border: 1px solid #fee2e2;
}

.modal-actions {
    margin-top: 1rem;
    display: flex;
    gap: 1rem;
}

.btn-cancel {
    flex: 1;
    padding: 0.85rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: white;
    color: var(--text-secondary);
    font-weight: 600;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel:hover { background: #f8fafc; }

.btn-submit {
    flex: 2;
    padding: 0.85rem;
    border-radius: 12px;
    border: none;
    background: #0071C5;
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-submit:hover:not(:disabled) { background: #005A9E; }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

@keyframes modalSlideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.spinner-small {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Toast */
.toast-notification {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
}

.toast-body {
    background: #10b981;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.4);
    animation: toastSlideDown 0.3s ease-out;
}

.toast-body.error {
    background: #ef4444;
    box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4);
}

.toast-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    margin-left: 4px;
    border-radius: 4px;
    color: white;
    opacity: 0.85;
    flex-shrink: 0;
}

.toast-close-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.15);
}

@keyframes toastSlideDown {
    from { transform: translateY(-40px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

@media (max-width: 640px) {
    .management-header { flex-direction: column; align-items: stretch; }
    .form-row { grid-template-columns: 1fr; }
}
</style>
